import React from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssessmentOutlined,
  ErrorOutline,
  LightbulbOutlined,
  ScheduleOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import MarkdownContent from './MarkdownContent';
import { getPriorityTone } from './insightUtils';

const TONE_COLORS = {
  error: '#c62828',
  warning: '#b54708',
  success: '#2e7d32',
  info: '#1565c0',
};

const InsightCard = ({ insight, freshnessLabel, onOpen }) => {
  const { t } = useTranslation();
  const tone = getPriorityTone(insight.priority);
  const accent = TONE_COLORS[tone];
  const methodLabel = t(`insights.methods.${insight.method}`);

  return (
    <Card
      variant="outlined"
      component="article"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderTop: `4px solid ${accent}`,
        minWidth: 0,
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: { xs: 2, sm: 2.5 } }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ color: accent, fontWeight: 700, lineHeight: 1.4 }}>
              {t(`insights.priorities.${String(insight.priority).toLowerCase()}`, {
                defaultValue: insight.priority,
              })}
            </Typography>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
              {insight.title || t('insights.untitled')}
            </Typography>
          </Box>
          {tone === 'error'
            ? <ErrorOutline sx={{ color: accent, flexShrink: 0 }} aria-hidden="true" />
            : <LightbulbOutlined sx={{ color: accent, flexShrink: 0 }} aria-hidden="true" />}
        </Stack>

        {insight.summary && (
          <MarkdownContent sx={{ mt: 1.25 }} color="text.secondary">
            {insight.summary}
          </MarkdownContent>
        )}

        {insight.recommendation && (
          <Box sx={{ mt: 1.5, p: 1.25, borderRadius: 1, bgcolor: '#f4f8f4', borderLeft: '3px solid #2e7d32' }}>
            <Typography variant="caption" sx={{ display: 'block', color: '#2e7d32', fontWeight: 700, mb: 0.25 }}>
              {t('insights.recommendedAction')}
            </Typography>
            <MarkdownContent>{insight.recommendation}</MarkdownContent>
          </Box>
        )}

        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Chip
            size="small"
            variant="outlined"
            icon={<AssessmentOutlined />}
            label={methodLabel}
            aria-label={t('insights.methodAria', { method: methodLabel })}
          />
          {freshnessLabel && (
            <Chip
              size="small"
              variant="outlined"
              icon={<ScheduleOutlined />}
              label={freshnessLabel}
              aria-label={t('insights.freshnessAria', { freshness: freshnessLabel })}
            />
          )}
        </Stack>

        {insight.confidence !== null && (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{t('insights.confidence')}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {Math.round(insight.confidence)}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={insight.confidence}
              aria-label={t('insights.confidenceAria', { confidence: Math.round(insight.confidence) })}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': { bgcolor: accent },
              }}
            />
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ px: { xs: 2, sm: 2.5 }, pb: 2, pt: 0 }}>
        <Button
          size="small"
          onClick={() => onOpen(insight)}
          aria-label={t('insights.viewDetailsAria', { title: insight.title || t('insights.untitled') })}
        >
          {t('insights.viewEvidence')}
        </Button>
      </CardActions>
    </Card>
  );
};

export default InsightCard;
